import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { BackButton, Button, Chip, Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { formatName } from '@/lib/format';
import { STATUS_LABEL, STATUS_TONE } from '@/modules/clientes/data';
import { AreaTotalCard } from '@/modules/clientes/components/AreaTotalCard';
import {
  useCliente,
  useDeleteCliente,
  useDeleteImovel,
  useUpdateCliente,
} from '@/modules/clientes/api';
import { decodeClienteId } from '@/modules/clientes/cliente-id';
import type { ClienteDetail, ImovelResumo, StatusCliente } from '@/modules/clientes/schemas';
import { ClienteBensTab } from '@/modules/clientes/components/ClienteBensTab';
import { ClienteDataView } from '@/modules/clientes/components/ClienteDataView';
import { ClienteDocumentosTab } from '@/modules/clientes/components/ClienteDocumentosTab';
import { ClienteHistoricoTab } from '@/modules/clientes/components/ClienteHistoricoTab';
import { ClienteRelatoriosTab } from '@/modules/clientes/components/ClienteRelatoriosTab';
import { ClienteResumo } from '@/modules/clientes/components/ClienteResumo';
import { CompletudeIndicator } from '@/modules/clientes/components/CompletudeIndicator';
import { DocumentosStatusIndicator } from '@/modules/clientes/components/DocumentosStatusIndicator';
import { ImovelEditModal } from '@/modules/clientes/components/ImovelEditModal';

const TABS = ['resumo', 'bens', 'docs', 'relatorios', 'hist'] as const;
type TabId = (typeof TABS)[number];

export const Route = createFileRoute('/_authed/clientes_/$id')({
  component: ClienteDetailPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    relatorio: string | undefined;
    tab: TabId | undefined;
    from_pipe: 'rural' | 'conformidade' | 'grupo-economico' | undefined;
    from_grupo: number | undefined;
  } => {
    const fromPipe = search.from_pipe;
    const validFromPipe =
      fromPipe === 'rural' || fromPipe === 'conformidade' || fromPipe === 'grupo-economico'
        ? fromPipe
        : undefined;
    const fromGrupoRaw = search.from_grupo;
    const fromGrupo =
      typeof fromGrupoRaw === 'number'
        ? fromGrupoRaw
        : typeof fromGrupoRaw === 'string' && /^\d+$/.test(fromGrupoRaw)
          ? Number(fromGrupoRaw)
          : undefined;
    return {
      relatorio: typeof search.relatorio === 'string' ? search.relatorio : undefined,
      tab:
        typeof search.tab === 'string' && (TABS as readonly string[]).includes(search.tab)
          ? (search.tab as TabId)
          : undefined,
      from_pipe: validFromPipe,
      from_grupo: validFromPipe === 'grupo-economico' ? fromGrupo : undefined,
    };
  },
});

type ImovelMode =
  | { kind: 'create'; cpfDono: string }
  | { kind: 'edit'; cpfDono: string; imovel: ImovelResumo }
  | null;

function ClienteDetailPage() {
  const { id } = Route.useParams();
  const { tab: tabSearch, from_pipe: fromPipe, from_grupo: fromGrupo } = Route.useSearch();
  const backSearch =
    fromPipe === 'grupo-economico'
      ? fromGrupo
        ? { pipe: 'grupo-economico' as const, grupo: fromGrupo }
        : { pipe: 'grupo-economico' as const }
      : fromPipe === 'conformidade'
        ? { pipe: 'conformidade' as const }
        : { pipe: 'rural' as const };
  const cpf = decodeClienteId(id);
  const navigate = useNavigate();
  const { data, isLoading, error } = useCliente(cpf);
  const del = useDeleteCliente();
  const delImovel = useDeleteImovel(cpf);

  const [tab, setTab] = useState<string>(tabSearch ?? 'resumo');
  const [imovelMode, setImovelMode] = useState<ImovelMode>(null);

  function onDeleteCliente() {
    if (!data) return;
    if (
      !confirm(
        `Excluir ${data.nome}? Imóveis, processos e documentos vinculados também serão removidos.`,
      )
    )
      return;
    del.mutate(cpf, { onSuccess: () => void navigate({ to: '/clientes', search: backSearch }) });
  }

  function onDeleteImovelClick(im: ImovelResumo) {
    if (!confirm(`Excluir imóvel "${im.denominacao || im.id}"? Ação irreversível.`)) return;
    delImovel.mutate(im.id);
  }

  const back = (
    <BackButton
      onClick={() => void navigate({ to: '/clientes', search: backSearch })}
      label="Voltar para Clientes"
    />
  );

  const actions = data ? (
    <Button
      variant="ghost"
      onClick={onDeleteCliente}
      disabled={del.isPending}
      className="text-danger hover:text-danger"
    >
      <Trash2 size={14} strokeWidth={1.5} />
      {del.isPending ? 'Excluindo…' : 'Excluir'}
    </Button>
  ) : null;

  return (
    <>
      <div className="flex min-h-full flex-col">
        <div className="flex-1 px-8 pb-8 pt-8">
          <Stack gap="md">
            <div className="flex items-center justify-between gap-4">
              {back}
              {actions}
            </div>

            {isLoading && <p className="text-sm text-text-muted">Carregando cliente…</p>}
            {error && (
              <p className="text-sm text-danger">
                {error instanceof ApiError ? error.message : 'Falha ao carregar cliente.'}
              </p>
            )}

            {data && (
              <>
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-wider text-text-muted">
                    Produtor Rural
                  </span>
                  <div className="text-[24px] font-semibold leading-[30px] text-text">
                    <EditableTitle cpf={cpf} name={formatName(data.nome)} />
                  </div>
                </div>
                <Header cliente={data} />
                <AreaTotalCard
                  propria={data.imoveis.reduce((s, im) => s + (im.area_total_ha ?? 0), 0)}
                  arrendada={(data.arrendamentos ?? []).reduce((s, a) => s + (a.area_ha ?? 0), 0)}
                />
                <ClienteDataView cliente={data} />

                <Tabs value={tab} onValueChange={setTab}>
                  <TabsList>
                    <TabsTrigger value="resumo">Resumo</TabsTrigger>
                    <TabsTrigger value="bens">Bens & Direitos</TabsTrigger>
                    <TabsTrigger value="docs">Documentos</TabsTrigger>
                    <TabsTrigger value="relatorios">Relatórios</TabsTrigger>
                    <TabsTrigger value="hist">Histórico</TabsTrigger>
                  </TabsList>

                  <TabsContent value="resumo">
                    <ClienteResumo
                      cliente={data}
                      onAddImovel={() => setImovelMode({ kind: 'create', cpfDono: cpf })}
                      onEditImovel={(im) =>
                        setImovelMode({ kind: 'edit', cpfDono: cpf, imovel: im })
                      }
                      onDeleteImovel={onDeleteImovelClick}
                    />
                  </TabsContent>
                  <TabsContent value="bens">
                    <ClienteBensTab cpf={cpf} />
                  </TabsContent>
                  <TabsContent value="docs">
                    <ClienteDocumentosTab cliente={data} />
                  </TabsContent>
                  <TabsContent value="relatorios">
                    <ClienteRelatoriosTab cliente={data} clienteId={id} />
                  </TabsContent>
                  <TabsContent value="hist">
                    <ClienteHistoricoTab />
                  </TabsContent>
                </Tabs>

                {delImovel.error && (
                  <p className="text-xs text-danger">
                    {delImovel.error instanceof ApiError
                      ? delImovel.error.message
                      : 'Falha ao excluir imóvel'}
                  </p>
                )}
              </>
            )}
          </Stack>
        </div>
      </div>

      <ImovelEditModal mode={imovelMode} onClose={() => setImovelMode(null)} />
    </>
  );
}

function Header({ cliente }: { cliente: ClienteDetail }) {
  const status = cliente.status as StatusCliente;
  const statusTone = STATUS_TONE[status] ?? 'neutral';
  const statusLabel = STATUS_LABEL[status] ?? status;

  return (
    <div className="flex items-center gap-2 flex-wrap self-start">
      <Chip tone={statusTone} size="sm" dot>
        {statusLabel}
      </Chip>
      <CompletudeIndicator cliente={cliente} />
      <DocumentosStatusIndicator cpf={cliente.cpf} />
    </div>
  );
}

function EditableTitle({ cpf, name }: { cpf: string; name: string }) {
  const update = useUpdateCliente(cpf);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [feedback, setFeedback] = useState<'ok' | 'err' | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(name);
  }, [name]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function flash(kind: 'ok' | 'err') {
    setFeedback(kind);
    setTimeout(() => setFeedback(null), kind === 'ok' ? 800 : 1500);
  }

  function save() {
    const trimmed = value.trim();
    if (!trimmed || trimmed === name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    update.mutate(
      { nome: trimmed },
      {
        onSuccess: () => {
          setEditing(false);
          flash('ok');
        },
        onError: () => flash('err'),
        onSettled: () => setSaving(false),
      },
    );
  }

  function cancel() {
    setValue(name);
    setEditing(false);
  }

  const bg =
    feedback === 'ok'
      ? 'rgba(5,150,105,.12)'
      : feedback === 'err'
        ? 'rgba(220,38,38,.12)'
        : undefined;

  // Modo edição: input grande + Salvar/Cancelar abaixo (sem auto-save).
  if (editing) {
    return (
      <span className="flex flex-col gap-2 items-start w-full">
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
          className="w-full bg-transparent border-b border-accent text-text font-semibold outline-none"
          style={{ fontSize: '24px', lineHeight: '30px' }}
        />
        <span className="flex items-center gap-2">
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
          <Button variant="secondary" size="sm" onClick={cancel} disabled={saving}>
            Cancelar
          </Button>
        </span>
      </span>
    );
  }

  // Modo leitura: nome + botão "Editar" que aparece no hover.
  return (
    <span
      className="group inline-flex items-center gap-2 px-1 -mx-1 rounded transition-colors hover:bg-surface2"
      style={{ background: bg }}
    >
      {name}
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Editar nome"
        aria-label="Editar nome"
        className="shrink-0 hidden group-hover:inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-text-2 transition-colors hover:bg-surface3 hover:text-text focus:outline-none"
      >
        <Pencil size={13} strokeWidth={1.5} />
        Editar
      </button>
    </span>
  );
}
